import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Box1, Card, Shop, TruckFast } from "iconsax-react";
import { useTranslation } from "react-i18next";

import { SefanoCheckbox } from "@/components/Atoms";
import { CreateShopStepHeading } from "@/components/Molecules";
import type { CreateShopShippingStepProps } from "./CreateShopShippingStep.types";
import * as styles from "./CreateShopShippingStep.styles";

const shippingIcons = {
  post: Box1,
  courier: TruckFast,
  pickup: Shop,
} as const;

export function CreateShopShippingStep({
  shippingOptions,
  smsOptions,
}: CreateShopShippingStepProps) {
  const { t } = useTranslation(["shops", "settings"]);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <>
      <CreateShopStepHeading
        titleKey="create.shipping.title"
        subtitleKey="create.shipping.subtitle"
        mobileSubtitleKey="create.shipping.mobileSubtitle"
      />

      <Box sx={styles.group}>
        <Typography sx={styles.title}>{t("settings:shipping.methodsTitle")}</Typography>

        {shippingOptions.map((option) => {
          const Icon = shippingIcons[option.key as keyof typeof shippingIcons] ?? Box1;

          return (
            <Box key={option.key} sx={styles.option(option.checked)} onClick={option.onToggle}>
              <Icon size={isDesktop ? 22 : 20} variant="Bold" color={theme.palette.primary.main} />
              <Box sx={styles.grow}>
                <Typography sx={styles.optionTitle}>{t(`settings:${option.titleKey}`)}</Typography>
                <Typography sx={styles.description}>
                  {t(`settings:${option.descriptionKey}`)}
                </Typography>
              </Box>
              <SefanoCheckbox checked={option.checked} />
            </Box>
          );
        })}
      </Box>

      {/* Online payment is always Zarinpal with split; nothing to choose here. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          p: 1.75,
          borderRadius: 1.75,
          bgcolor: "background.default",
        }}
      >
        <Card size={22} variant="Bold" color={theme.palette.primary.main} />
        <Box>
          <Typography sx={styles.optionTitle}>{t("settings:shipping.gatewayTitle")}</Typography>
          <Typography sx={styles.description}>
            {t("settings:shipping.gatewayDescription")}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.divider}>
        <Typography sx={styles.title}>{t("settings:shipping.smsTitle")}</Typography>

        {smsOptions.map((option) => (
          <Box key={option.key} sx={styles.sms}>
            <Box>
              <Typography sx={styles.optionTitle}>{t(`settings:${option.titleKey}`)}</Typography>
              <Typography sx={styles.description}>
                {t(`settings:${option.descriptionKey}`)}
              </Typography>
            </Box>
            <SefanoCheckbox checked={option.checked} onChange={option.onToggle} />
          </Box>
        ))}
      </Box>
    </>
  );
}
